
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ConsultModal from '../../components/feature/ConsultModal';
import ScrollToTop from '../../components/feature/ScrollToTop';
import LanguageGuard from '../../components/feature/LanguageGuard';
import SeoHead from '../../components/feature/SeoHead';
import BlogHero from './components/BlogHero';
import FeaturedPost from './components/FeaturedPost';
import CategoryFilter from './components/CategoryFilter';
import BlogCard from './components/BlogCard';
import { blogPosts, blogCategories } from '../../mocks/blogPosts';
import { getLangPath, type Language } from '../../router/langRoutes';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://thedaeg.com';

const seoData: Record<string, { title: string; description: string; keywords: string }> = {
  vi: {
    title: 'Tin Tức Định Cư Latvia 2025 - Blog & Cẩm Nang Cho Người Việt | The Daeg',
    description:
      'Cập nhật tin tức định cư Latvia mới nhất 2025. Chính sách, kinh nghiệm thực tế, hướng dẫn thủ tục đầu tư định cư Latvia cho người Việt Nam. Blog chia sẻ từ chuyên gia di trú.',
    keywords:
      'tin tức định cư Latvia, blog định cư Latvia, kinh nghiệm định cư Latvia, chính sách định cư Latvia 2025, hướng dẫn định cư châu Âu, thủ tục thẻ tạm trú Latvia',
  },
  en: {
    title: 'Latvia Residency News 2025 - Blog & Guide for Vietnamese | The Daeg',
    description:
      'Latest Latvia residency news 2025. Policies, real experiences, step-by-step guide for Latvia investment immigration for Vietnamese. Expert immigration blog.',
    keywords:
      'Latvia residency news, Latvia immigration blog, Latvia residency experience, Latvia policy 2025, European immigration guide',
  },
  lv: {
    title: 'Latvijas Uzturēšanās Jaunumi 2025 - Blogs | The Daeg International',
    description:
      'Jaunākās ziņas par uzturēšanās atļauju Latvijā 2025. Politika, pieredze, ceļvedis Latvijas ieguldījumu imigrācijai.',
    keywords:
      'Latvijas uzturēšanās jaunumi, imigrācijas blogs Latvija, Latvijas politika 2025',
  },
};

const BlogPage = () => {
  const { i18n } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('Tất cả');

  const lang = (i18n.language in seoData ? i18n.language : 'vi') as Language;
  const seo = seoData[lang];

  const featuredPost = blogPosts.find((p) => p.featured);
  const filteredPosts = blogPosts.filter((p) => {
    if (activeCategory === 'Tất cả') return !p.featured;
    return p.category === activeCategory && !p.featured;
  });

  const blogListingSchema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Blog Định Cư Latvia - The Daeg International',
    description: seo.description,
    url: `${SITE_URL}${getLangPath('latviaBlog', lang)}`,
    publisher: {
      '@type': 'Organization',
      name: 'The Daeg International Trade',
      url: SITE_URL,
    },
    blogPost: blogPosts.slice(0, 6).map((post) => ({
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.excerpt,
      datePublished: post.date,
      url: `${SITE_URL}${getLangPath('latviaBlog', lang)}/${post.id}`,
      image: post.image,
      author: {
        '@type': 'Organization',
        name: 'The Daeg International Trade',
      },
    })),
  };

  return (
    <LanguageGuard>
      <div className="min-h-screen bg-white">
        <SeoHead
          title={seo.title}
          description={seo.description}
          keywords={seo.keywords}
          canonicalPath={getLangPath('latviaBlog', lang)}
          schemaJson={blogListingSchema}
        />
        <header>
          <Navbar onConsultClick={() => setIsModalOpen(true)} />
        </header>
        <main>
          <BlogHero />

          {featuredPost && (
            <section className="py-6 sm:py-10 md:py-14 bg-gray-50">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 className="text-base sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-5 md:mb-6 font-serif flex items-center gap-1.5 sm:gap-2">
                  <i className="ri-fire-fill text-gold-500 w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center"></i>
                  Bài Viết Nổi Bật
                </h2>
                <FeaturedPost {...featuredPost} />
              </div>
            </section>
          )}

          <section className="py-6 sm:py-10 md:py-16 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col gap-3 sm:gap-4 mb-5 sm:mb-8 md:mb-10">
                <h2 className="text-base sm:text-xl md:text-2xl font-bold text-gray-900 font-serif">
                  Tất Cả Bài Viết
                </h2>
                <CategoryFilter
                  categories={blogCategories}
                  activeCategory={activeCategory}
                  onCategoryChange={setActiveCategory}
                />
              </div>

              {filteredPosts.length > 0 ? (
                <div className="flex flex-col gap-3 sm:hidden">
                  {filteredPosts.map((post) => (
                    <BlogCard key={post.id} {...post} />
                  ))}
                </div>
              ) : null}
              {filteredPosts.length > 0 ? (
                <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8">
                  {filteredPosts.map((post) => (
                    <BlogCard key={post.id} {...post} />
                  ))}
                </div>
              ) : null}
              {filteredPosts.length === 0 && (
                <div className="text-center py-12 md:py-20">
                  <div className="w-14 h-14 md:w-20 md:h-20 flex items-center justify-center mx-auto mb-3 bg-gray-100 rounded-full">
                    <i className="ri-article-line text-2xl md:text-4xl text-gray-400"></i>
                  </div>
                  <p className="text-xs md:text-base text-gray-500">Chưa có bài viết trong danh mục này.</p>
                </div>
              )}
            </div>
          </section>

          <section className="py-8 sm:py-12 md:py-16 bg-gradient-to-br from-gold-50 to-amber-50 border-t border-gold-200">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h4 className="text-base sm:text-xl md:text-2xl font-bold text-gray-900 mb-2 sm:mb-3 md:mb-4 font-serif">
                <a href={getLangPath('contact', lang)} className="hover:text-gold-700 transition-colors">
                  Bạn Cần Tư Vấn Về Định Cư Latvia?
                </a>
              </h4>
              <p className="text-[13px] sm:text-sm md:text-base text-gray-600 mb-4 sm:mb-5 md:mb-6 leading-relaxed">
                Đội ngũ chuyên gia của The Daeg International sẵn sàng hỗ trợ bạn trên hành trình{' '}
                <strong>định cư Latvia</strong>. Đăng ký tư vấn miễn phí ngay hôm nay.
              </p>
              <button
                onClick={() => setIsModalOpen(true)}
                className="w-full sm:w-auto px-7 sm:px-8 py-3 bg-gold-600 text-white text-sm md:text-base font-semibold rounded-lg hover:bg-gold-700 transition-colors shadow-lg whitespace-nowrap cursor-pointer"
              >
                Đăng Ký Tư Vấn Miễn Phí
              </button>
            </div>
          </section>
        </main>
        <Footer />
        <ConsultModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
        <ScrollToTop />
      </div>
    </LanguageGuard>
  );
};

export default BlogPage;
